import { AnalyticsBannerLink } from '@/shared/features/analytics/presentation/components';
import { cn } from '@/shared/utils/cn';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/shared/widgets/carousel';
import SectionTitle from './SectionTitle';
import { useIsDesktop } from '@/core/hooks/useMediaQuery';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import {
  HOME_PAGE_BANNER_CLICKED,
  HOME_PAGE_BANNER_VIEWED,
  NEW_HOME_BANNER_CAROUSEL_SECTION_NAME,
  NEW_HOME_BANNER_TYPE_OFFER,
  newHomeOfferBannerBaseEventProperties,
  newHomeOfferBannerClickEventProperties,
  resolveNewHomeOfferBannerDisplayName,
} from '../analytics/homePageBannerCarousel';
import {
  resolveItemImageUrl,
  type CarouselPresentationVm,
  type SectionItemPresentationVm,
} from '../viewModels/newHome.vm';
import Autoplay from 'embla-carousel-autoplay';
import { useMemo, useCallback } from 'react';

interface CarouselSectionProps {
  data: CarouselPresentationVm;
  sectionKey: string;
  eventLabel: string;
}

function CarouselSlide({
  item,
  shape,
  aspectRatio,
  showTitle,
  blankImageUrl,
}: {
  item: SectionItemPresentationVm;
  shape: string;
  aspectRatio: string;
  showTitle: boolean;
  blankImageUrl: string;
}) {
  const isDesktop = useIsDesktop();
  const src = resolveItemImageUrl(item, isDesktop);
  const bannerDisplayName = resolveNewHomeOfferBannerDisplayName(item, isDesktop);
  const eventProperties = newHomeOfferBannerClickEventProperties({
    bannerDisplayName,
    bannerPosition: item.position,
    targetUrl: item.link,
  });
  return (
    <AnalyticsBannerLink
      to={item.link}
      shouldRefresh={item.shouldRefresh}
      bannerName={bannerDisplayName}
      bannerType={NEW_HOME_BANNER_TYPE_OFFER}
      sectionName={NEW_HOME_BANNER_CAROUSEL_SECTION_NAME}
      eventName={HOME_PAGE_BANNER_CLICKED}
      eventProperties={eventProperties}
      className="block"
      title={bannerDisplayName || undefined}
      aria-label={bannerDisplayName || undefined}
    >
      <img
        src={src}
        alt={bannerDisplayName}
        className={cn(
          'w-full h-auto object-cover',
          shape === 'circle' ? 'rounded-full' : 'rounded-lg',
          aspectRatio === '1:1' && 'aspect-square',
          aspectRatio === '3:4' && 'aspect-[3/4]',
          aspectRatio === '4:3' && 'aspect-[4/3]'
        )}
        loading="lazy"
        onError={(e) => {
          if (blankImageUrl) {
            (e.target as HTMLImageElement).src = blankImageUrl;
          }
        }}
      />
      {showTitle && (
        <p className="mt-1 text-center text-xs">{item.title}</p>
      )}
    </AnalyticsBannerLink>
  );
}

export default function CarouselSection({ data, sectionKey: _sectionKey, eventLabel }: CarouselSectionProps) {
  const isDesktop = useIsDesktop();
  const { analytics, isInitialized } = useAnalytics();
  const plugins = useMemo(
    () => [Autoplay({ delay: data.slideInterval, stopOnInteraction: true })],
    [data.slideInterval]
  );

  const onUserBannerViewed = useCallback(
    (selectedIndex: number) => {
      if (!analytics || !isInitialized) return;
      const selectedItem = data.items[selectedIndex];
      if (!selectedItem) return;
      const bannerDisplayName = resolveNewHomeOfferBannerDisplayName(selectedItem, isDesktop);
      try {
        analytics.trackEvent(
          HOME_PAGE_BANNER_VIEWED,
          newHomeOfferBannerBaseEventProperties({
            bannerDisplayName,
            bannerPosition: selectedItem.position,
          }),
        );
      } catch (error) {
        console.error('Error tracking Home Page Banner Viewed:', error);
      }
    },
    [analytics, isInitialized, data.items, isDesktop],
  );

  return (
    <section
      className="w-full"
      style={{ backgroundColor: data.bgColor, color: data.textColor }}
      data-analytics-section-name={eventLabel}
    >
      {data.title && <SectionTitle title={data.title} />}

      <div className="w-full px-3 pb-4">
        <Carousel
          opts={{ align: 'start', loop: true }}
          plugins={plugins}
          onUserSelect={onUserBannerViewed}
        >
          <CarouselContent className="-ml-2">
            {data.items.map((item) => (
              <CarouselItem key={item.position} className="basis-[70%] pl-2">
                <CarouselSlide
                  item={item}
                  shape={data.itemShape}
                  aspectRatio={data.itemAspectRatio}
                  showTitle={data.showItemTitle}
                  blankImageUrl={data.blankImageUrl}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </section>
  );
}
